import { isClientEnv } from '@cdc-internal/fe-toolbox/utils/env'

import { currentAppEnvConfigs } from '@/configs/env'

import { defaultFetcher, FetcherFunctionType } from '.'
import { checkBusinessErrorWith200Response } from './hooks'
import { getApiRequestBaseUrl, Version } from './utils'

/* --------------------------- Price basic service -------------------------- */

const commonPriceApiConfig = {
  prefix: '/price',
  serverDomain: currentAppEnvConfigs.backendHosts.price,
  serverDomainInternal: currentAppEnvConfigs.backendHosts.priceInternal,
}
export const priceApiBaseUrl = getApiRequestBaseUrl(commonPriceApiConfig)
export const priceFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(priceApiBaseUrl + path, requestOptions, {
    afterResponseHooks: [checkBusinessErrorWith200Response],
    ...extendedOptions,
  })

const priceApiBaseUrlV2 = getApiRequestBaseUrl({
  ...commonPriceApiConfig,
  version: Version.V2,
})
export const priceFetcherV2: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(priceApiBaseUrlV2 + path, ...restParams)

const priceApiBaseUrlV3 = getApiRequestBaseUrl({
  ...commonPriceApiConfig,
  version: Version.V3,
})
export const priceFetcherV3: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(priceApiBaseUrlV3 + path, ...restParams)

/* ------------------------------ Meta service ------------------------------ */

const commonMetaApiConfig = {
  serverDomain: currentAppEnvConfigs.backendHosts.price,
  serverDomainInternal: currentAppEnvConfigs.backendHosts.tokenMetaInternal,
  prefix: '/meta',
}

export const metaApiBaseUrl = getApiRequestBaseUrl(commonMetaApiConfig)
export const metaFetcher: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(metaApiBaseUrl + path, ...restParams)

const metaApiBaseUrlV2 = getApiRequestBaseUrl({
  ...commonMetaApiConfig,
  version: Version.V2,
})
export const metaFetcherV2: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(metaApiBaseUrlV2 + path, ...restParams)

const commonMarketApiConfig = {
  ...commonPriceApiConfig,
  prefix: '/market',
}

const marketApiBaseUrl = getApiRequestBaseUrl(commonMarketApiConfig)
export const marketFetcher: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(marketApiBaseUrl + path, ...restParams)

const marketApiBaseUrlV2 = getApiRequestBaseUrl({
  ...commonMarketApiConfig,
  version: Version.V2,
})
export const marketFetcherV2: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(marketApiBaseUrlV2 + path, ...restParams)

/* ----------------------------- Price Member service ----------------------------- */
const commonPriceMemberApiConfig = {
  prefix: '/member',
  serverDomain: currentAppEnvConfigs.backendHosts.priceMember,
}

const priceMemberApiBaseUrlV2 = getApiRequestBaseUrl({
  ...commonPriceMemberApiConfig,
  version: Version.V2,
})

export const memberFetcherV2: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(priceMemberApiBaseUrlV2 + path, requestOptions, {
    ...extendedOptions,
  })

/* ----------------------------- Popular service ---------------------------- */

const popularApiBaseUrl = getApiRequestBaseUrl({
  ...commonPriceApiConfig,
  prefix: '/popular',
})
export const popularFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(popularApiBaseUrl + path, requestOptions, {
    ...extendedOptions,
  })

/* ----------------------------- App Public API ----------------------------- */

const commonAppPublicApiConfig = {
  prefix: '/api/v1/public',
  serverDomain: currentAppEnvConfigs.backendHosts.app,
  withVersion: false,
}

export const appPublicApiBaseUrl = getApiRequestBaseUrl(commonAppPublicApiConfig)
export const appPublicFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(appPublicApiBaseUrl + path, requestOptions, {
    afterResponseHooks: [checkBusinessErrorWith200Response],
    ...extendedOptions,
  })

/* --------------------------- Payload CMS service -------------------------- */

const commonPayloadCmsApiConfig = {
  prefix: '/api',
  serverDomain: currentAppEnvConfigs.payloadCmsDomain,
}
export const payloadCmsApiBaseUrl = getApiRequestBaseUrl(commonPayloadCmsApiConfig)
export const payloadCmsFetcher: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(payloadCmsApiBaseUrl + path, ...restParams)

/* --------------------------- coinsaiSunshine service -------------------------- */

const commonMarketfeedApiConfig = {
  prefix: '/marketfeed',
  serverDomain: currentAppEnvConfigs.backendHosts.coinsaiSunshine,
}
export const marketfeedApiBaseUrl = getApiRequestBaseUrl(commonMarketfeedApiConfig)
export const marketfeedFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(marketfeedApiBaseUrl + path, requestOptions, {
    ...extendedOptions,
  })

const coinsaiSunshineTokenApiConfig = {
  prefix: '/token',
  serverDomain: currentAppEnvConfigs.backendHosts.coinsaiSunshine,
}
export const coinsaiSunshineTokenApiBaseUrl = getApiRequestBaseUrl(coinsaiSunshineTokenApiConfig)
export const coinsaiSunshineTokenFetcher: FetcherFunctionType = (
  path,
  requestOptions,
  extendedOptions,
) =>
  defaultFetcher(coinsaiSunshineTokenApiBaseUrl + path, requestOptions, {
    ...extendedOptions,
  })

const cryptoCoinInfoApiConfig = {
  prefix: '/api',
  serverDomain: currentAppEnvConfigs.backendHosts.cryptoCoinInfo,
}
export const cryptoCoinInfoApiBaseUrl = getApiRequestBaseUrl({
  ...cryptoCoinInfoApiConfig,
  version: Version.V2,
})

// In local development the browser cannot call the coin-info host directly
// (CORS is only opened for crypto.com origins), so client-side requests go
// through the dev-only same-origin rewrite `/_proxy/coin-info/*` (see
// next.config.ts). Server-side always uses the absolute upstream URL.
// NODE_ENV is inlined at build time, so the dev branch is tree-shaken out of
// production bundles.
const getCryptoCoinInfoApiBaseUrl = () =>
  process.env.NODE_ENV === 'development' && isClientEnv
    ? '/_proxy/coin-info/api/v2'
    : cryptoCoinInfoApiBaseUrl

export const cryptoCoinInfoFetcherV2: FetcherFunctionType = (
  path,
  requestOptions,
  extendedOptions,
) =>
  defaultFetcher(getCryptoCoinInfoApiBaseUrl() + path, requestOptions, {
    ...extendedOptions,
  })

/* --------------------------- Token Management service -------------------------- */

const tokenMgmtApiConfig = {
  prefix: '/meta',
  serverDomain: currentAppEnvConfigs.backendHosts.tokenMgmt,
  withVersion: false,
}
export const tokenMgmtApiBaseUrl = getApiRequestBaseUrl(tokenMgmtApiConfig)
export const tokenMgmtFetcher: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(tokenMgmtApiBaseUrl + path, ...restParams)

/* --------------------------- Equity Market Data service -------------------------- */

const equityMarketDataApiConfig = {
  prefix: '/api',
  serverDomain: currentAppEnvConfigs.backendHosts.equityMarketData,
}
export const equityMarketDataApiBaseUrl = getApiRequestBaseUrl(equityMarketDataApiConfig)

// In local development the browser cannot call the equity-market-data host
// directly (CORS is only opened for crypto.com origins), so client-side
// requests go through the dev-only same-origin rewrite
// `/_proxy/equity-market-data/*` (see next.config.ts). Server-side always uses
// the absolute upstream URL. NODE_ENV is inlined at build time, so the dev
// branch is tree-shaken out of production bundles.
const getEquityMarketDataApiBaseUrl = () =>
  process.env.NODE_ENV === 'development' && isClientEnv
    ? '/_proxy/equity-market-data/api/v1'
    : equityMarketDataApiBaseUrl

export const equityMarketDataFetcher: FetcherFunctionType = (
  path,
  requestOptions,
  extendedOptions,
) =>
  defaultFetcher(getEquityMarketDataApiBaseUrl() + path, requestOptions, {
    ...extendedOptions,
  })

/* --------------------------- Equity Price service -------------------------- */

// Batch latest-prices API for the stocks table. Lives under the /public
// namespace (no auth), unlike the rest of equity-price-api.
const equityPriceApiConfig = {
  prefix: '/public/api',
  serverDomain: currentAppEnvConfigs.backendHosts.equityPrice,
}
export const equityPriceApiBaseUrl = getApiRequestBaseUrl(equityPriceApiConfig)

// Same dev CORS situation as equity-market-data: browser requests in dev go
// through the same-origin `/_proxy/equity-price/*` rewrite (next.config.ts).
const getEquityPriceApiBaseUrl = () =>
  process.env.NODE_ENV === 'development' && isClientEnv
    ? '/_proxy/equity-price/public/api/v1'
    : equityPriceApiBaseUrl

export const equityPriceFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(getEquityPriceApiBaseUrl() + path, requestOptions, {
    ...extendedOptions,
  })

/* --------------------------- Trading Product service -------------------------- */

// Public trading-product API (equity baskets). Lives under /api/public — no
// auth, CORS whitelist managed by Infra.
const tradingProductApiConfig = {
  prefix: '/api/public',
  serverDomain: currentAppEnvConfigs.backendHosts.tradingProduct,
}
export const tradingProductApiBaseUrl = getApiRequestBaseUrl(tradingProductApiConfig)

// Same dev CORS situation as equity-market-data: browser requests in dev go
// through the same-origin `/_proxy/trading-product/*` rewrite (next.config.ts).
const getTradingProductApiBaseUrl = () =>
  process.env.NODE_ENV === 'development' && isClientEnv
    ? '/_proxy/trading-product/api/public/v1'
    : tradingProductApiBaseUrl

export const tradingProductFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(getTradingProductApiBaseUrl() + path, requestOptions, {
    ...extendedOptions,
  })

/* --------------------------- Knock-Out Predictions service -------------------------- */

const knockOutApiConfig = {
  prefix: '/predictions/public',
  serverDomain: currentAppEnvConfigs.backendHosts.knockOut,
  withVersion: false,
}

const knockOutApiBaseUrl = getApiRequestBaseUrl(knockOutApiConfig)

// Same dev CORS situation as the knock-out derivatives API below: client-side
// requests in dev go through the same-origin `/_proxy/knock-out/*` rewrite.
const getKnockOutApiBaseUrl = () =>
  process.env.NODE_ENV === 'development' && isClientEnv
    ? '/_proxy/knock-out/predictions/public'
    : knockOutApiBaseUrl

export const knockOutFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(getKnockOutApiBaseUrl() + path, requestOptions, {
    ...extendedOptions,
  })

// Prediction 无前缀 CDC 命名空间（/public，monaco-app 同款）。cdc-web 的
// knock-out 服务在 hotfix/prediction-public-with-auth 里把 events / nav-tree /
// contracts 从 /og/public 迁到这里——og 前缀版是 OG 产品命名空间（含
// "Pro Football Hub" 等 OG 独有节点并错配地区配置），CDC 不应使用。与
// Derivatives 产品共用同一个 /public 前缀（见下方 knockOutDerivativesFetcher）；
// 客户端在 dev 走同一条 /_proxy/knock-out/public 同源 rewrite。
const knockOutPublicApiBaseUrl = getApiRequestBaseUrl({
  prefix: '/public',
  serverDomain: currentAppEnvConfigs.backendHosts.knockOut,
  withVersion: false,
})
const getKnockOutPublicApiBaseUrl = () =>
  process.env.NODE_ENV === 'development' && isClientEnv
    ? '/_proxy/knock-out/public'
    : knockOutPublicApiBaseUrl
export const knockOutPublicFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(getKnockOutPublicApiBaseUrl() + path, requestOptions, {
    ...extendedOptions,
  })

/* --------------------------- Knock-Out Derivatives service -------------------------- */

// Derivatives products API lives under /public (different prefix from the
// predictions API above). In local development the browser cannot call the
// knock-out host directly (CORS is only opened for crypto.com origins), so
// client-side requests go through the dev-only same-origin rewrite
// `/_proxy/knock-out/*` (see next.config.ts). Server-side always uses the
// absolute upstream URL. NODE_ENV is inlined at build time, so the dev branch
// is tree-shaken out of production bundles.
const knockOutDerivativesAbsoluteBaseUrl = getApiRequestBaseUrl({
  prefix: '/public',
  serverDomain: currentAppEnvConfigs.backendHosts.knockOut,
  withVersion: false,
})

const getKnockOutDerivativesBaseUrl = () =>
  process.env.NODE_ENV === 'development' && isClientEnv
    ? '/_proxy/knock-out/public'
    : knockOutDerivativesAbsoluteBaseUrl

export const knockOutDerivativesFetcher: FetcherFunctionType = (
  path,
  requestOptions,
  extendedOptions,
) =>
  defaultFetcher(getKnockOutDerivativesBaseUrl() + path, requestOptions, {
    ...extendedOptions,
  })

/* --------------------------- Localization service -------------------------- */

const localizationApiConfig = {
  prefix: '/api',
  serverDomain: currentAppEnvConfigs.backendHosts.localization,
  withVersion: false,
}
export const localizationApiBaseUrl = getApiRequestBaseUrl(localizationApiConfig)
export const localizationFetcher: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(localizationApiBaseUrl + path, ...restParams)

/* --------------------------- Rewards Campaign service -------------------------- */

const rewardsCampaignApiConfig = {
  prefix: '/internal/api/campaign',
  serverDomain: currentAppEnvConfigs.backendHosts.rewardsCampaign,
}
export const rewardsCampaignApiBaseUrl = getApiRequestBaseUrl(rewardsCampaignApiConfig)
export const rewardsCampaignFetcher: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(rewardsCampaignApiBaseUrl + path, ...restParams)
